import React, { useState, useEffect, useCallback } from "react";
import { Plus, User, Building, Trash2 } from "lucide-react";
import { ApiService } from "../../../services/api";
import checkPermission from "../../../utils/permission";
import { useModal } from "../../../hooks/useModal";
import AddParticipantModal, { AddParticipantFormData } from "../../../components/modals/AddParticipantModal";

interface SurveyParticipant {
  id: string;
  type: "USER" | "DEPARTMENT";
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  department?: {
    id: string;
    name: string;
    color?: string;
  };
}

interface ParticipantManagerProps {
  surveyId: string;
  onComplete?: () => void;
}

export default function ParticipantManager({ surveyId, onComplete }: ParticipantManagerProps) {
  const { showAlert, showConfirm } = useModal();
  const [participants, setParticipants] = useState<SurveyParticipant[]>([]);
  const [loading, setLoading] = useState(true);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);

  const canUpdateSurvey = checkPermission("survey_update");

  const loadParticipants = useCallback(async () => {
    try {
      const response = await ApiService.getSurveyParticipants(surveyId);
      const participants = Array.isArray(response) ? response : response?.data || [];
      setParticipants(participants);
    } catch (error: any) {
      console.error("Katılımcılar yüklenirken hata:", error);
      if (!error.message?.includes("Survey not found")) {
        await showAlert({
          title: "Hata",
          message: error.message || "Katılımcılar yüklenirken hata oluştu.",
          type: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  }, [surveyId, showAlert]);

  useEffect(() => {
    loadParticipants();
  }, [loadParticipants]);

  const handleAddParticipant = async (formData: AddParticipantFormData) => {
    if (!canUpdateSurvey) {
      await showAlert({
        title: "Yetki Hatası",
        message: "Anket güncelleme yetkiniz bulunmamaktadır.",
        type: "error",
      });
      return;
    }

    setAddLoading(true);

    try {
      if (formData.type === "USER") {
        for (const userId of formData.selectedUsers) {
          await ApiService.createSurveyParticipant(surveyId, {
            type: "USER",
            userId: userId,
          });
        }
      } else {
        for (const departmentId of formData.selectedDepartments) {
          await ApiService.createSurveyParticipant(surveyId, {
            type: "DEPARTMENT",
            departmentId: departmentId,
          });
        }
      }

      setAddModalOpen(false);
      await loadParticipants();
    } catch (error: any) {
      console.error("Katılımcı ekleme hatası:", error);

      let errorMessage = error.message || "Katılımcı eklenirken hata oluştu.";

      if (error.message && error.message.includes("Bu kullanıcının departmanı zaten bir anket katılımcıları arasında")) {
        errorMessage =
          "Bu kullanıcının departmanı zaten anket katılımcıları arasında bulunmaktadır. Aynı departmandan hem bireysel kullanıcı hem de departman katılımcısı ekleyemezsiniz.";
      }

      await showAlert({
        title: "Hata",
        message: errorMessage,
        type: "error",
      });
    } finally {
      setAddLoading(false);
    }
  };

  const handleDeleteParticipant = async (participant: SurveyParticipant) => {
    if (!canUpdateSurvey) {
      await showAlert({
        title: "Yetki Hatası",
        message: "Anket güncelleme yetkiniz bulunmamaktadır.",
        type: "error",
      });
      return;
    }

    const confirmed = await showConfirm({
      title: "Katılımcıyı Çıkar",
      message: `"${
        participant.type === "USER"
          ? `${participant.user?.firstName || ""} ${participant.user?.lastName || ""}`.trim() || "İsimsiz Kullanıcı"
          : participant.department?.name || "İsimsiz Departman"
      }" katılımcısını çıkarmak istediğinizden emin misiniz?`,
      confirmText: "Çıkar",
      confirmButtonVariant: "danger",
    });

    if (confirmed) {
      setDeleteLoading(participant.id);

      try {
        await ApiService.deleteSurveyParticipant(surveyId, participant.id);
        await loadParticipants();
      } catch (error: any) {
        console.error("Katılımcı silme hatası:", error);
        await showAlert({
          title: "Hata",
          message: error.message || "Katılımcı çıkarılırken hata oluştu.",
          type: "error",
        });
      } finally {
        setDeleteLoading(null);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        {canUpdateSurvey && (
          <button
            onClick={() => setAddModalOpen(true)}
            className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm flex items-center gap-1">
            <Plus className="w-4 h-4" />
            Katılımcı Ekle
          </button>
        )}
      </div>

      {participants.length === 0 ? (
        <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <User className="w-12 h-12 mx-auto text-gray-400 mb-3" />
          <p className="text-lg font-medium">Henüz Katılımcı Eklenmemiş</p>
          <p className="text-sm">Anketinize katılımcı eklemek için yukarıdaki butonu kullanın.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <User className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-blue-900">Kullanıcı Katılımcıları</h3>
                <p className="text-sm text-blue-700">{participants.filter((p) => p.type === "USER").length} kullanıcı</p>
              </div>
            </div>
            {participants.filter((p) => p.type === "USER").length === 0 ? (
              <div className="text-center py-6 text-blue-600 bg-blue-100 rounded-lg">
                <p className="text-sm">Henüz kullanıcı katılımcısı yok.</p>
              </div>
            ) : (
              <div className="border border-blue-200 rounded-lg overflow-hidden bg-white">
                <div className="max-h-80 overflow-y-auto divide-y bg-white">
                  {participants
                    .filter((p) => p.type === "USER")
                    .map((participant) => (
                      <div
                        key={participant.id}
                        className="w-full px-4 py-3 text-left text-sm hover:bg-gray-50 flex items-center gap-3 bg-white transition-colors">
                        <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-semibold text-blue-600">
                            {(participant.user?.firstName || "").charAt(0) || "U"}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-gray-900 truncate">
                            {`${participant.user?.firstName || ""} ${participant.user?.lastName || ""}`.trim() || "İsimsiz Kullanıcı"}
                          </div>
                          {participant.user?.email && <div className="text-xs text-gray-500 truncate">{participant.user.email}</div>}
                        </div>

                        {canUpdateSurvey && (
                          <button
                            onClick={() => handleDeleteParticipant(participant)}
                            disabled={deleteLoading === participant.id}
                            className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded disabled:opacity-50 transition-colors flex-shrink-0"
                            title="Kullanıcıyı Çıkar">
                            {deleteLoading === participant.id ? (
                              <div className="animate-spin h-3 w-3 border-2 rounded-full border-t-transparent border-red-500"></div>
                            ) : (
                              <Trash2 className="w-3 h-3" />
                            )}
                          </button>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>

          <div className="bg-green-50 rounded-xl p-6 border border-green-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Building className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-green-900">Departman Katılımcıları</h3>
                <p className="text-sm text-green-700">{participants.filter((p) => p.type === "DEPARTMENT").length} departman</p>
              </div>
            </div>
            {participants.filter((p) => p.type === "DEPARTMENT").length === 0 ? (
              <div className="text-center py-6 text-green-600 bg-green-100 rounded-lg">
                <p className="text-sm">Henüz departman katılımcısı yok.</p>
              </div>
            ) : (
              <div className="border border-green-200 rounded-lg overflow-hidden bg-white">
                <div className="max-h-80 overflow-y-auto divide-y bg-white">
                  {participants
                    .filter((p) => p.type === "DEPARTMENT")
                    .map((participant) => (
                      <div
                        key={participant.id}
                        className="w-full px-4 py-3 text-left text-sm hover:bg-gray-50 flex items-center gap-2 bg-white transition-colors">
                        {participant.department?.color && (
                          <div
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{
                              backgroundColor: participant.department.color,
                            }}
                          />
                        )}
                        <span className="truncate flex-1 text-gray-900 font-medium">
                          {participant.department?.name || "İsimsiz Departman"}
                        </span>

                        {canUpdateSurvey && (
                          <button
                            onClick={() => handleDeleteParticipant(participant)}
                            disabled={deleteLoading === participant.id}
                            className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded disabled:opacity-50 transition-colors flex-shrink-0 ml-2"
                            title="Departmanı Çıkar">
                            {deleteLoading === participant.id ? (
                              <div className="animate-spin h-3 w-3 border-2 rounded-full border-t-transparent border-red-500"></div>
                            ) : (
                              <Trash2 className="w-3 h-3" />
                            )}
                          </button>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <AddParticipantModal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSubmit={handleAddParticipant}
        isLoading={addLoading}
        title="Ankete Katılımcı Ekle"
        excludeUserIds={participants.filter((p) => p.type === "USER" && p.user?.id).map((p) => p.user!.id)}
        excludeDepartmentIds={participants.filter((p) => p.type === "DEPARTMENT" && p.department?.id).map((p) => p.department!.id)}
      />

      {onComplete && (
        <div className="flex justify-end mt-6 pt-4 border-t border-gray-200">
          <button onClick={onComplete} className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm">
            Tamamla
          </button>
        </div>
      )}
    </div>
  );
}
